import get from 'lodash.get';
import {
    MEASURE_DISPLAY_NAME,
    MEASURE as SHARED_MEASURE_CONSTANTS,
    D8_MEASURES,
    MEASURE_TITLE as SHARED_MEASURE_TITLE,
    MEASURES_WITH_PERCENTAGE as SHARED_MEASURES_WITH_PERCENTAGE,
} from '../shared/MeasuresConstants';
import { COMPOSITE, LEXILE_TITLE } from '../shared/CommonConstants';
import { getMeasureTitleByKey } from '../../utils/shared/measureUtils';

export const MEASURE = {
    ...SHARED_MEASURE_CONSTANTS,
    TRO: 'TRO',
    DO: 'DO',
    RAN: 'RAN',
    WR: 'WR',
    LEXILE: 'LEXILE',
};

export const MEASURE_TITLE = {
    ...SHARED_MEASURE_TITLE,
    ORF_OVERALL: 'Oral Reading Fluency',
    NWF: 'Nonsense Word Fluency',
    OL: 'Oral Language',
    TRO: 'Text Reading Online',
    DO: 'Decoding Online',
    RAN: 'Phonological Processing',
    [MEASURE.LC]: 'Letter Combination',
    [MEASURE.AP]: 'Advanced Phonics',
    [MEASURE.RW]: 'Regular Words',
    [MEASURE.IW]: 'Irregular Words',
};

export const PARENT_MEASURE = {
    ORF_OVERALL: 'ORF-OVERALL',
    NWF: 'NWF',
};

export const BURST_MEASURE = {
    VOC: 'VOC',
    DEC_IW: 'DEC-IW',
    DEC_RW: 'DEC-RW',
    DEC_LC: 'DEC-LC',
    DEC_AP: 'DEC-AP',
    CS_R: 'CS (R)',
    CS_L: 'CS (L)',
    CS_I: 'CS (I)',
    IW: 'IW',
    RW: 'RW',
    LC: 'LC',
    AP: 'AP',
};

const LetterCombinations = 'Letter Combinations';
const AdvancedPhonics = 'Advanced Phonics';
const RegularWords = 'Regular Words';
const IrregularWords = 'Irregular Words';

export const DYSLEXIA_MEASURES = [MEASURE.SPELLING, MEASURE.RAN];
export const FR_READING_VIEW_MEASURES = [
    MEASURE.OVERALL,
    MEASURE.PSF,
    MEASURE.NWF_CLS,
    MEASURE.NWF_WRC,
    MEASURE.WRF,
    MEASURE.ORF,
    MEASURE.MAZE,
];
export const FR_EXPRESS_VIEW_MEASURES = [MEASURE.TRO, MEASURE.DO];
export const SUMMARY_MEASURES = [
    MEASURE.OVERALL,
    MEASURE.LEXILE,
    MEASURE.TRC,
    MEASURE.OL,
    MEASURE.TRO,
    MEASURE.DO,
    MEASURE.VOCABO,
    MEASURE.SPELLING,
    MEASURE.RAN,
];
export const PARENT_MEASURES = [PARENT_MEASURE.ORF_OVERALL, PARENT_MEASURE.NWF];
export const TRC_OR_TRO = [MEASURE.TRC, MEASURE.TRO];
export const PM_MEASURES_EXCEPTIONS = [
    MEASURE.TRO,
    MEASURE.DO,
    MEASURE.VOCABO,
    MEASURE.SPELLING,
    MEASURE.RAN,
    MEASURE.OVERALL,
    MEASURE.OL,
];
export const BURST_MEASURES = [
    BURST_MEASURE.DEC_IW,
    BURST_MEASURE.DEC_RW,
    BURST_MEASURE.DEC_LC,
    BURST_MEASURE.DEC_AP,
    BURST_MEASURE.CS_R,
    BURST_MEASURE.CS_L,
    BURST_MEASURE.CS_I,
    BURST_MEASURE.VOC,
];

export const D8_MEASURES_WITH_LEXILE = [
    D8_MEASURES[0],
    MEASURE.LEXILE,
    ...D8_MEASURES.filter(measure => measure !== MEASURE.OVERALL),
];

export const DECODING_SUBSCORE_MEASURES = {
    CVCC_CCVC: 'cvcc-ccvc',
    CCVCC: 'ccvcc',
    VCE: 'vce',
    LC: 'lc',
    IE: 'ie',
    S2_OS: 's2-os',
    S2_CS: 's2-cs',
    S2_VT: 's2-vt',
    S2_VCE: 's2-vce',
    PREF: 'pref',
    SUF: 'suf',
    S2: 's2',
    S3: 's3',
    S4: 's4',
    PREF_SUF: 'pref-suf',
    S34: 's34',
};

export const DECODING_SUBSCORE_MEASURES_LABELS = {
    [DECODING_SUBSCORE_MEASURES.CVCC_CCVC]: 'CVCC, CCVC',
    [DECODING_SUBSCORE_MEASURES.CCVCC]: 'CCVCC',
    [DECODING_SUBSCORE_MEASURES.VCE]: 'VCe',
    [DECODING_SUBSCORE_MEASURES.LC]: 'Letter Combinations',
    [DECODING_SUBSCORE_MEASURES.IE]: 'Inflected Endings',
    [DECODING_SUBSCORE_MEASURES.S2_OS]: '2 Syl - Open',
    [DECODING_SUBSCORE_MEASURES.S2_CS]: '2 Syl - Closed',
    [DECODING_SUBSCORE_MEASURES.S2_VT]: '2 Syl - Vowel Teams',
    [DECODING_SUBSCORE_MEASURES.S2_VCE]: '2 Syl - VCe',
    [DECODING_SUBSCORE_MEASURES.PREF]: 'Prefixes',
    [DECODING_SUBSCORE_MEASURES.SUF]: 'Suffixes',
    [DECODING_SUBSCORE_MEASURES.S2]: '2 Syllables',
    [DECODING_SUBSCORE_MEASURES.S3]: '3 Syllables',
    [DECODING_SUBSCORE_MEASURES.S4]: '4 Syllables',
    [DECODING_SUBSCORE_MEASURES.PREF_SUF]: 'Prefixes and Suffixes',
    [DECODING_SUBSCORE_MEASURES.S34]: '3-4 Syllables',
};

export const ALL_MEASURES = [
    {
        key: MEASURE.OVERALL,
        name: COMPOSITE,
        title: COMPOSITE,
    },
    {
        key: MEASURE.LEXILE,
        name: LEXILE_TITLE,
        title: LEXILE_TITLE,
    },
    {
        key: MEASURE.LNF,
        name: MEASURE.LNF,
        title: MEASURE_TITLE[MEASURE.LNF],
        displayName: 'Letter Naming Fluency',
    },
    {
        key: MEASURE.PSF,
        name: MEASURE.PSF,
        title: MEASURE_TITLE[MEASURE.PSF],
        displayName: 'Phonemic Segmentation Fluency',
    },
    {
        key: MEASURE.NWF_CLS,
        name: MEASURE.NWF_CLS,
        title: MEASURE_TITLE[MEASURE.NWF_CLS],
        displayName: 'Correct Letter Sounds',
    },
    {
        key: MEASURE.NWF_WRC,
        name: MEASURE.NWF_WRC,
        title: MEASURE_TITLE[MEASURE.NWF_WRC],
        displayName: 'Words Recoded Correctly',
    },
    {
        key: MEASURE.WRF,
        name: MEASURE.WRF,
        title: MEASURE_TITLE[MEASURE.WRF],
        displayName: 'Word Reading Fluency',
    },
    {
        key: MEASURE.ORF_ACC,
        name: 'ORF-Accu',
        title: MEASURE_TITLE[MEASURE.ORF_ACC],
        displayName: 'Accuracy',
    },
    {
        key: MEASURE.ORF,
        name: MEASURE.ORF,
        title: MEASURE_TITLE[MEASURE.ORF],
        displayName: 'Fluency',
    },
    {
        key: PARENT_MEASURE.ORF_OVERALL,
        name: MEASURE.ORF,
        title: MEASURE_TITLE.ORF_OVERALL,
        displayName: 'Oral Reading Fluency',
    },
    {
        key: PARENT_MEASURE.NWF,
        name: PARENT_MEASURE.NWF,
        title: MEASURE_TITLE[MEASURE.NWF],
        displayName: 'Nonsense Word Fluency',
    },
    {
        key: MEASURE.MAZE,
        name: 'Maze',
        title: MEASURE_TITLE[MEASURE.MAZE],
        displayName: 'Basic Comprehension',
    },
    {
        key: MEASURE.TRC,
        name: MEASURE.TRC,
        title: MEASURE_TITLE[MEASURE.TRC],
        displayName: 'Text Reading and Comprehension',
    },
    {
        key: MEASURE.OL,
        name: MEASURE.OL,
        title: MEASURE_TITLE[MEASURE.OL],
        displayName: 'Oral Language',
    },
    {
        key: MEASURE.TRO,
        name: MEASURE.TRO,
        title: MEASURE_TITLE[MEASURE.TRO],
        displayName: 'Text Reading Online',
    },
    {
        key: MEASURE.DO,
        name: MEASURE.DO,
        title: MEASURE_TITLE[MEASURE.DO],
        displayName: 'Decoding Online',
    },
    {
        key: MEASURE.VOCABO,
        name: MEASURE_DISPLAY_NAME[MEASURE.VOCABO],
        title: MEASURE_TITLE[MEASURE.VOCABO],
        displayName: 'Vocabulary',
    },
    {
        key: MEASURE.SPELLING,
        name: 'SPELL',
        title: MEASURE_TITLE[MEASURE.SPELLING],
        displayName: 'Spelling',
    },
    {
        key: MEASURE.RAN,
        name: MEASURE.RAN,
        title: MEASURE_TITLE[MEASURE.RAN],
        displayName: 'Rapid Automatized Naming',
    },
    {
        key: MEASURE.AP,
        name: MEASURE.AP,
        title: MEASURE_TITLE[MEASURE.AP],
        displayName: `Decode - ${MEASURE_TITLE[MEASURE.AP]}`,
    },
    {
        key: MEASURE.LC,
        name: MEASURE.LC,
        title: MEASURE_TITLE[MEASURE.LC],
        displayName: `Decode - ${MEASURE_TITLE[MEASURE.LC]}`,
    },
    {
        key: MEASURE.RW,
        name: MEASURE.RW,
        title: MEASURE_TITLE[MEASURE.RW],
        displayName: `Decode - ${MEASURE_TITLE[MEASURE.RW]}`,
    },
    {
        key: MEASURE.IW,
        name: MEASURE.IW,
        title: MEASURE_TITLE[MEASURE.IW],
        displayName: `Decode - ${MEASURE_TITLE[MEASURE.IW]}`,
    },
];

export const MEASURES = ALL_MEASURES.filter(measure => !PARENT_MEASURES.includes(measure.key));

export const MEASURES_KEYS = MEASURES.map(measure => measure.key);

export const HOME_CONNECT_AVAILABLE_MEASURES = [
    { key: MEASURE.OVERALL, title: 'DIBELS 8' },
    { key: MEASURE.TRC, title: MEASURE.TRC },
    { key: MEASURE.OL, title: MEASURE_TITLE[MEASURE.OL] },
    { key: MEASURE.TRO, title: MEASURE.TRO },
    { key: MEASURE.DO, title: MEASURE_TITLE[MEASURE.DO] },
    {
        key: MEASURE.VOCABO,
        title: getMeasureTitleByKey(ALL_MEASURES, MEASURE.VOCABO),
    },
    { key: MEASURE.SPELLING, title: getMeasureTitleByKey(ALL_MEASURES, MEASURE.SPELLING) },
    { key: MEASURE.RAN, title: MEASURE.RAN },
];

export const HOME_CONNECT_LETTER_MEASURES = [
    ...D8_MEASURES.filter(
        filteredMeasures =>
            ![MEASURE.RW, MEASURE.IW, MEASURE.AP, MEASURE.LC].includes(filteredMeasures),
    ),
    MEASURE.TRC,
    MEASURE.OL,
    MEASURE.DO,
    MEASURE.TRO,
    MEASURE.VOCABO,
    MEASURE.SPELLING,
    MEASURE.RAN,
];

const CS_TITLE = 'Comprehension Skills';
const CS_CODE = 'CS';
export const BURST_MEASURE_GENUS = [
    { key: BURST_MEASURE.VOC, name: 'VOC', code: 'VOC', title: 'Vocabulary' },
    { key: BURST_MEASURE.DEC_IW, name: 'IW', code: 'DEC-IW', title: IrregularWords },
    { key: BURST_MEASURE.DEC_RW, name: 'RW', code: 'DEC-RW', title: RegularWords },
    { key: BURST_MEASURE.DEC_LC, name: 'LC', code: 'DEC-LC', title: LetterCombinations },
    { key: BURST_MEASURE.DEC_AP, name: 'AP', code: 'DEC-AP', title: AdvancedPhonics },
    { key: BURST_MEASURE.CS_R, name: 'CS Retell/Recall', code: CS_CODE, title: CS_TITLE },
    { key: BURST_MEASURE.CS_L, name: 'CS Literal', code: CS_CODE, title: CS_TITLE },
    { key: BURST_MEASURE.CS_I, name: 'CS Inference', code: CS_CODE, title: CS_TITLE },
];

export const MEASURES_WITH_PERCENTAGE = [...SHARED_MEASURES_WITH_PERCENTAGE];

export const MEASURES_WITHOUT_GOAL = [MEASURE.TRO, MEASURE.LEXILE];

export const getBurstMeasureTitleByKey = key =>
    get(
        BURST_MEASURE_GENUS.find(measure => measure.key === key),
        'title',
        '',
    );

export const getBurstMeasureNameByKey = key =>
    get(
        BURST_MEASURE_GENUS.find(measure => measure.key === key),
        'name',
        '',
    );

export const getBurstMeasureCodeByKey = key =>
    get(
        BURST_MEASURE_GENUS.find(measure => measure.key === key),
        'code',
        '',
    );

export const hasLastInterventionSkill = (interventionSkills, measure) => {
    const measureKeys = interventionSkills.map(skill =>
        get(
            BURST_MEASURE_GENUS.find(genus => genus.code === skill.code),
            'key',
            '',
        ),
    );
    return measureKeys.includes(measure);
};

export const AVAILABLE_TITLE_MEASURE = [
    SHARED_MEASURE_CONSTANTS.ORF_ACC,
    SHARED_MEASURE_CONSTANTS.ORF,
    SHARED_MEASURE_CONSTANTS.NWF_WRC,
    SHARED_MEASURE_CONSTANTS.NWF_CLS,
];

export const HOME_CONNECT_MEASURE_TITLES = {
    [MEASURE.OVERALL]: 'DIBELS 8',
    [MEASURE.TRC]: MEASURE.TRC,
    [MEASURE.OL]: MEASURE_TITLE[MEASURE.OL],
    [MEASURE.TRO]: MEASURE.TRO,
    [MEASURE.DO]: MEASURE_TITLE[MEASURE.DO],
    [MEASURE.VOCABO]: MEASURE_TITLE[MEASURE.VOCABO],
    [MEASURE.SPELLING]: MEASURE_TITLE[MEASURE.SPELLING],
    [MEASURE.RAN]: MEASURE.RAN,
};
